/**
 * DiffPanel — карточки правок (EVENT_PROPOSED_EDIT) с Apply/Reject.
 *
 * Backend эмитит `proposed_edit` с полями `edit_id`, `path`, `before`, `after`,
 * `description`. Здесь рендерим side-by-side diff с кнопками. Ответ — POST
 * `/api/v1/workspace/apply` или `/workspace/reject` (см. lib/api.ts).
 *
 * Реализует Пункт 0.2 / фидбэк Сессии 7.
 */

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, FileEdit, Loader2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/cn";
import { useChatsStore } from "../../store/chats";
import { applyProposedEdit, rejectProposedEdit } from "../../lib/api";
import type { ProposedEditEvent } from "../../lib/types";

export function DiffPanel() {
  const session = useChatsStore((s) => s.sessions.find((x) => x.id === s.activeId));
  const edits = session?.proposedEdits || [];
  const pending = edits.filter((e) => e.state === "pending");

  if (!pending.length) return null;

  return (
    <div className="space-y-2">
      {edits.map((edit) => (
        <DiffCard key={edit.edit_id} edit={edit} />
      ))}
    </div>
  );
}

function DiffCard({
  edit,
}: {
  edit: ProposedEditEvent & { state: "pending" | "applied" | "rejected"; error?: string };
}) {
  const markApplied = useChatsStore((s) => s.markEditApplied);
  const markRejected = useChatsStore((s) => s.markEditRejected);
  const setEditError = useChatsStore((s) => s.setEditError);
  const [busy, setBusy] = useState<"apply" | "reject" | null>(null);
  const [expanded, setExpanded] = useState(true);

  const lines = useMemo(() => buildDiffLines(edit.before, edit.after), [edit.before, edit.after]);
  const stats = useMemo(() => {
    let added = 0, removed = 0;
    for (const l of lines) {
      if (l.kind === "add") added++;
      if (l.kind === "del") removed++;
    }
    return { added, removed };
  }, [lines]);

  async function onApply() {
    setBusy("apply");
    try {
      await applyProposedEdit(edit.edit_id);
      markApplied(edit.edit_id);
    } catch (err) {
      setEditError(edit.edit_id, err instanceof Error ? err.message : "Ошибка");
    } finally {
      setBusy(null);
    }
  }

  async function onReject() {
    setBusy("reject");
    try {
      await rejectProposedEdit(edit.edit_id);
      markRejected(edit.edit_id);
    } catch (err) {
      setEditError(edit.edit_id, err instanceof Error ? err.message : "Ошибка");
    } finally {
      setBusy(null);
    }
  }

  const stateBadge = {
    pending: { label: "Ожидает", color: "bg-yellow-500/20 text-yellow-300" },
    applied: { label: "Применено", color: "bg-green-500/20 text-green-300" },
    rejected: { label: "Отклонено", color: "bg-red-500/20 text-red-300" },
  }[edit.state];

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="card overflow-hidden border border-border/40"
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-border/40 flex items-center gap-2 bg-bg-subtle/40">
        <FileEdit className="size-4 text-accent-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-small font-medium truncate">{edit.path}</div>
          {edit.description && (
            <div className="text-[0.75rem] text-fg-muted truncate">{edit.description}</div>
          )}
        </div>
        <span className={cn("px-2 py-0.5 rounded text-[0.7rem] font-medium shrink-0", stateBadge.color)}>
          {stateBadge.label}
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 rounded hover:bg-bg-subtle text-fg-muted shrink-0"
          aria-label={expanded ? "Свернуть" : "Развернуть"}
        >
          {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
      </div>

      {/* Stats */}
      <div className="px-3 py-1.5 text-[0.75rem] border-b border-border/30 flex gap-3 text-fg-muted">
        <span className="text-green-400">+{stats.added}</span>
        <span className="text-red-400">−{stats.removed}</span>
        {edit._truncated && <span className="text-yellow-400">⚠ обрезано</span>}
      </div>

      {/* Diff body */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="font-mono text-[0.78rem] max-h-80 overflow-auto bg-bg/40 p-2">
              {lines.length === 0 ? (
                <div className="text-fg-muted px-2">(нет изменений)</div>
              ) : (
                lines.map((l, i) => (
                  <div
                    key={i}
                    className={cn(
                      "px-2 leading-5 whitespace-pre",
                      l.kind === "add" && "bg-green-500/10 text-green-200",
                      l.kind === "del" && "bg-red-500/10 text-red-200",
                      l.kind === "ctx" && "text-fg-muted",
                    )}
                  >
                    <span className="select-none mr-2 opacity-60">
                      {l.kind === "add" ? "+" : l.kind === "del" ? "−" : " "}
                    </span>
                    {l.text}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error */}
      {edit.error && (
        <div className="px-3 py-2 bg-red-500/10 border-t border-red-500/20 text-red-300 text-small flex items-center gap-2">
          <AlertCircle className="size-3.5 shrink-0" />
          <span className="truncate">{edit.error}</span>
        </div>
      )}

      {/* Actions */}
      {edit.state === "pending" && (
        <div className="px-3 py-2 border-t border-border/40 flex gap-2 justify-end bg-bg/30">
          <button
            onClick={onReject}
            disabled={busy !== null}
            className="btn-ghost gap-1.5 text-red-300 hover:bg-red-500/10"
          >
            {busy === "reject" ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
            Отклонить
          </button>
          <button
            onClick={onApply}
            disabled={busy !== null}
            className="btn-primary gap-1.5"
          >
            {busy === "apply" ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
            Применить
          </button>
        </div>
      )}
    </motion.div>
  );
}

interface DiffLine {
  kind: "add" | "del" | "ctx";
  text: string;
}

/**
 * Простой LCS-based line diff. Достаточен для отображения коротких правок,
 * которые обычно делает агент. Для крупных файлов (>2k строк) деградирует
 * до side-by-side полного содержимого без LCS.
 */
function buildDiffLines(before: string, after: string): DiffLine[] {
  const a = before.split("\n");
  const b = after.split("\n");

  // Бойкот квадратной сложности на больших файлах.
  if (a.length * b.length > 200_000) {
    const out: DiffLine[] = [];
    a.forEach((t) => out.push({ kind: "del", text: t }));
    b.forEach((t) => out.push({ kind: "add", text: t }));
    return out;
  }

  const m = a.length;
  const n = b.length;
  // dp[i][j] = LCS length для a[i..] и b[j..]
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const lines: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      lines.push({ kind: "ctx", text: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      lines.push({ kind: "del", text: a[i] });
      i++;
    } else {
      lines.push({ kind: "add", text: b[j] });
      j++;
    }
  }
  while (i < m) lines.push({ kind: "del", text: a[i++] });
  while (j < n) lines.push({ kind: "add", text: b[j++] });
  return lines;
}
